/**
 * Zustand store for onboarding state.
 *
 * Tracks the current onboarding step, the contextual story and
 * contextual tip visibility.
 */

import { create } from "zustand";
import {
  getDesktopChatAuthScope,
  isCurrentDesktopChatAuthScope,
} from "@/desktop/desktop-chat-auth-scope";
import {
  clearOnboardingCompletionIntent,
  restoreOnboardingCompletionIntent,
  saveOnboardingCompletionIntent,
  type OnboardingCompletion,
} from "./onboarding-completion-intent";
import {
  mirroredStore,
  withMutationEmitSuppressed,
} from "@/lib/mirrored-store";
import type { OnboardingRestartReason, OnboardingStep } from "@/api/onboarding";
import { isTauri } from "@/lib/is-tauri";
import { DESKTOP_ONBOARDING_CONTENT_VERSION } from "@/desktop/onboarding/onboarding-content-version";
import { getWindowRoleSync } from "@/desktop/use-window-role";
import type { ContextualStoryState } from "@/desktop/onboarding/contextual-story-state";
import { onPostHogReady, posthog } from "@/lib/posthog";
import {
  clearOnboardingLocalState,
  getOnboardingBool,
  getOnboardingLocalStateUser,
  ONBOARDING_REPLAY_EXISTING_SCREEN_RECORDING_GRANT_KEY,
  setOnboardingBool,
  setOnboardingLocalStateUser,
} from "@/lib/onboarding-local-state";

let progressGeneration = 0;

/** Invalidated by account/session changes, hydration and explicit restarts. */
export function captureOnboardingProgressGuard(): () => boolean {
  const generation = progressGeneration;
  const epoch = useOnboardingStore.getState().progressEpoch;
  const userId = getOnboardingLocalStateUser();
  const authScope = getDesktopChatAuthScope();
  return () =>
    generation === progressGeneration &&
    epoch === useOnboardingStore.getState().progressEpoch &&
    userId === getOnboardingLocalStateUser() &&
    (!authScope || isCurrentDesktopChatAuthScope(authScope));
}

function trackOnboarding(
  eventName: string,
  props: Record<string, unknown> = {},
) {
  const userId = getOnboardingLocalStateUser();
  const capture = () => {
    if (getOnboardingLocalStateUser() !== userId) return;
    posthog.capture(eventName, {
      ...props,
      onboarding_version: isTauri() ? "desktop-v2" : "web-v1",
    });
  };
  if (posthog.__loaded) capture();
  else onPostHogReady(capture);
}

export type TipId = "memory" | "slash_commands" | "at_mention";

function readLocalGateState(): { introSeen: boolean } {
  return {
    introSeen: getOnboardingBool("denker.onboarding.intro_seen"),
  };
}

// Only a genuinely fresh user (no persisted step at all) re-arms the
// intro replay gate. Any persisted step — including the legacy
// `desktop_permissions` — must NOT clear `intro_seen`: wiping it on every
// hydration made the intro auto-rerun forever for users stuck on the
// legacy value (2026-07-03 onboarding-rerun bug).
function shouldReplayDesktopIntro(step: string | null): boolean {
  return isTauri() && step === null;
}

interface OnboardingState {
  contextualStory: ContextualStoryState | null;
  /** A retry clicked on any display is repaired by the owner. */
  contextualStartupRetryId: string | null;
  /** Current step from backend. Null = not started. */
  step: OnboardingStep | null;
  /** Last step confirmed by the API; separate from optimistic UI progress. */
  persistedStep: OnboardingStep | null;
  stepPersistError: OnboardingStep | null;
  /** Terminal intent awaiting a confirmed save, including after cold launch. */
  pendingCompletion: OnboardingCompletion | null;
  /** Owner's active completion request; transient and mirrored, never restored. */
  completionInFlight: string | null;
  /** Mirrored identity for rejecting delayed completion events after restart. */
  progressEpoch: string;
  prepareCompletion: (step: OnboardingCompletion, requestId?: string) => void;
  /** True after auth has loaded the backend onboarding step at least once. */
  stepHydrated: boolean;
  /** Desktop onboarding content version last completed/skipped (backend). */
  desktopOnboardingVersion: number;
  /** True when the user completed onboarding during this session (not just loaded as completed). */
  justCompleted: boolean;
  /** True when companion_install was triggered from a completed/skipped state (reconnection, not first-time). */
  reconnecting: boolean;
  /** Tips the user has already dismissed. */
  seenTips: Set<string>;
  /** Number of messages sent (for tip triggers). */
  messageCount: number;
  // ── Cross-webview gate state ──────────────────────────────────────
  /** Whether the intro animation has been shown this install. */
  introSeen: boolean;

  setStep: (step: OnboardingStep | null) => void;
  /**
   * Intermediate steps advance optimistically; terminal steps wait for a save.
   * Resolves on success or failure for legacy callers. Inspect persistedStep
   * and stepPersistError when completion must be acknowledged.
   */
  advanceStep: (step: OnboardingStep) => Promise<void>;
  restarting: boolean;
  setRestarting: (restarting: boolean) => void;
  resetToOrient: (reason: OnboardingRestartReason) => Promise<void>;
  dismissTip: (tipId: TipId) => void;
  initFromUser: (
    step: string | null,
    seenTips: string[],
    userId: string,
    desktopOnboardingVersion: number,
  ) => void;
  incrementMessageCount: () => void;

  // Gate mark actions — write through store AND localStorage (owner only).
  markIntroSeen: () => void;
  clearUser: () => void;
}

// Server writes that mutate onboarding_step are serialized at the API
// boundary (see stepWriteChain in @/api/onboarding) — pre-granted
// permission chains fire several advances in one tick, and concurrent
// PATCHes raced last-write-wins on the server, committing an EARLIER step
// after a later one (2026-07-08 prod, five PATCHes in one second
// regressed vision → hotkeys). The API-level chain orders every writer
// (this store, the desktop bridge, the web overlay's
// named update) so the store itself no longer needs its own chain.

export const useOnboardingStore = create<OnboardingState>()(
  mirroredStore("onboarding", (set) => ({
    step: null,
    persistedStep: null,
    stepPersistError: null,
    pendingCompletion: null,
    completionInFlight: null,
    progressEpoch: crypto.randomUUID(),
    stepHydrated: false,
    desktopOnboardingVersion: 0,
    restarting: false,
    setRestarting: (restarting) => {
      const startsRestart =
        restarting && !useOnboardingStore.getState().restarting;
      if (startsRestart) progressGeneration++;
      set({
        restarting,
        ...(startsRestart
          ? { progressEpoch: crypto.randomUUID(), completionInFlight: null }
          : {}),
      });
    },
    justCompleted: false,
    reconnecting: false,
    contextualStory: null,
    contextualStartupRetryId: null,
    seenTips: new Set(),
    messageCount: 0,
    introSeen: false,

    setStep: (step) => set({ step }),

    prepareCompletion: (step, requestId) => {
      const state = useOnboardingStore.getState();
      if (
        state.restarting ||
        (state.pendingCompletion && state.pendingCompletion !== step)
      )
        return;
      saveOnboardingCompletionIntent(step, state.step, state.persistedStep);
      set({
        pendingCompletion: step,
        stepPersistError: null,
        ...(requestId ? { completionInFlight: requestId } : {}),
      });
    },

    advanceStep: (step): Promise<void> => {
      const state = useOnboardingStore.getState();
      const terminal = step === "completed" || step === "skipped";
      if (state.restarting || (state.pendingCompletion && !terminal))
        return Promise.resolve();
      const previous = state.step;
      const isCurrent = captureOnboardingProgressGuard();
      const recordsContentVersion = isTauri() && terminal;
      if (terminal) {
        useOnboardingStore.getState().prepareCompletion(step);
        if (useOnboardingStore.getState().pendingCompletion !== step)
          return Promise.resolve();
      } else {
        set((state) => ({
          step,
          stepPersistError: null,
          contextualStory: null,
          contextualStartupRetryId: null,
          justCompleted: false,
          reconnecting:
            step === "companion_install" &&
            (state.step === "completed" || state.step === "skipped")
              ? true
              : state.reconnecting,
        }));
      }
      // The lazy import avoids the notification/error-middleware store cycle.
      const write = import("@/api/onboarding").then((onboardingApi) => {
        if (!isCurrent()) throw new Error("Onboarding progress scope changed");
        return onboardingApi.updateStep(
          step,
          recordsContentVersion
            ? DESKTOP_ONBOARDING_CONTENT_VERSION
            : undefined,
          isCurrent,
        );
      });
      return write.then(
        () => {
          if (!isCurrent()) return;
          const {
            persistedStep: from,
            desktopOnboardingVersion,
            reconnecting,
          } = useOnboardingStore.getState();
          if (terminal) clearOnboardingCompletionIntent();
          set({
            persistedStep: step,
            stepPersistError: null,
            ...(terminal
              ? {
                  step,
                  pendingCompletion: null,
                  completionInFlight: null,
                  contextualStory: null,
                  contextualStartupRetryId: null,
                  justCompleted: step === "completed" && !reconnecting,
                  reconnecting: false,
                }
              : {}),
            ...(recordsContentVersion
              ? {
                  desktopOnboardingVersion: Math.max(
                    desktopOnboardingVersion,
                    DESKTOP_ONBOARDING_CONTENT_VERSION,
                  ),
                }
              : {}),
          });
          if (from === step) return;
          trackOnboarding("onboarding_step_advanced", { from, to: step });
          if (step === "skipped")
            trackOnboarding("onboarding_skipped", { from_step: from });
        },
        () => {
          if (!isCurrent()) return;
          const state = useOnboardingStore.getState();
          if (
            terminal ? state.pendingCompletion === step : state.step === step
          ) {
            set({ stepPersistError: step });
          }
          trackOnboarding("onboarding_step_failed", {
            from: previous,
            to: step,
          });
        },
      );
    },

    resetToOrient: async (reason) => {
      const wasRestarting = useOnboardingStore.getState().restarting;
      // `restarting` hides every onboarding surface while the server resets,
      // so nothing local is touched until the reset is known to have landed.
      progressGeneration++;
      set({
        restarting: true,
        completionInFlight: null,
        ...(wasRestarting ? {} : { progressEpoch: crypto.randomUUID() }),
      });
      const isCurrent = captureOnboardingProgressGuard();
      try {
        // Desktop resets to `desktop_intro` — the entry step of the
        // cursor-led flow. Never persist the legacy `desktop_permissions`
        // here: a reset interrupted mid-walk would leave the DB on a value
        // the flow can never terminate from (2026-07-03 onboarding-rerun bug).
        const target: OnboardingStep = isTauri() ? "desktop_intro" : "orient";
        // Same server field, different endpoint — ordering against any queued
        // updateStep PATCH is guaranteed by the API-level chain.
        const onboardingApi = await import("@/api/onboarding");
        if (!isCurrent()) return;
        await onboardingApi.resetOnboarding(target, reason, isCurrent);
        if (!isCurrent()) return;
        clearOnboardingCompletionIntent();
        clearOnboardingLocalState(reason === "manual" ? "all" : "replay");
        if (isTauri()) {
          const [{ cancelAllScenes }, { useDenkerController }] =
            await Promise.all([
              import("@/desktop/onboarding/scene-engine"),
              import("@/desktop/onboarding/denker-cursor-controller"),
            ]);
          if (!isCurrent()) return;
          cancelAllScenes();
          useDenkerController.getState().reset();
          // Restarting replays the flow; it must not behave like a
          // fresh-install permission reset when OS grants already exist.
          setOnboardingBool(
            ONBOARDING_REPLAY_EXISTING_SCREEN_RECORDING_GRANT_KEY,
            true,
          );
          if (reason === "manual") {
            // A manual restart keeps TTS silent until a voice is chosen again.
            // A content-version replay keeps the user's chosen voice.
            const { useTtsSettingsStore } =
              await import("@/stores/tts-settings-store");
            if (!isCurrent()) return;
            const tts = useTtsSettingsStore.getState();
            tts.setEnabled(false);
            tts.setVoiceURI(null);
            tts.setDenkerVoice(null);
            tts.setPremiumAvailable(false);
          }
        }
        trackOnboarding("onboarding_restarted", { reason });
        set({
          step: target,
          pendingCompletion: null,
          completionInFlight: null,
          persistedStep: target,
          stepPersistError: null,
          stepHydrated: true,
          justCompleted: false,
          reconnecting: false,
          contextualStory: null,
          contextualStartupRetryId: null,
          introSeen: false,
        });
      } finally {
        if (isCurrent()) set({ restarting: wasRestarting });
      }
    },

    dismissTip: (tipId) => {
      void import("@/api/onboarding")
        .then((onboardingApi) => onboardingApi.dismissTip(tipId))
        .catch(() => {});
      trackOnboarding("onboarding_tip_dismissed", { tip_id: tipId });
      set((state) => {
        const next = new Set(state.seenTips);
        next.add(tipId);
        return { seenTips: next };
      });
    },

    initFromUser: (step, seenTips, userId, desktopOnboardingVersion) => {
      progressGeneration++;
      const accountChanged = getOnboardingLocalStateUser() !== userId;
      setOnboardingLocalStateUser(userId, isTauri() ? "desktop" : "web");
      // Auth resolves independently in each webview. A late mirror's REST
      // snapshot must not rewind the owner's live progress after signup.
      // Its local identity is still needed for account-scoped preferences.
      if (isTauri() && getWindowRoleSync() !== "owner") return;
      const localGateState = readLocalGateState();
      if (shouldReplayDesktopIntro(step)) {
        setOnboardingBool("denker.onboarding.intro_seen", false);
        localGateState.introSeen = false;
      }
      set({
        step: step as OnboardingStep | null,
        pendingCompletion: restoreOnboardingCompletionIntent(step),
        completionInFlight: null,
        progressEpoch: crypto.randomUUID(),
        contextualStartupRetryId: null,
        restarting: false,
        ...(accountChanged
          ? { contextualStory: null, justCompleted: false, reconnecting: false }
          : {}),
        persistedStep: step as OnboardingStep | null,
        stepPersistError: null,
        stepHydrated: true,
        seenTips: new Set(seenTips),
        desktopOnboardingVersion,
        ...localGateState,
      });
    },

    incrementMessageCount: () =>
      set((state) => ({ messageCount: state.messageCount + 1 })),

    markIntroSeen: () => {
      if (getWindowRoleSync() === "owner") {
        setOnboardingBool("denker.onboarding.intro_seen", true);
      }
      set({ introSeen: true });
    },

    clearUser: () => {
      progressGeneration++;
      setOnboardingLocalStateUser(null, isTauri() ? "desktop" : "web");
      const clear = () =>
        set({
          step: null,
          progressEpoch: crypto.randomUUID(),
          pendingCompletion: null,
          completionInFlight: null,
          contextualStory: null,
          contextualStartupRetryId: null,
          justCompleted: false,
          reconnecting: false,
          persistedStep: null,
          stepPersistError: null,
          stepHydrated: false,
          desktopOnboardingVersion: 0,
          restarting: false,
          seenTips: new Set(),
          introSeen: false,
        });
      // Adopting the new auth scope clears every webview locally. Mirrors
      // must not send that clear back after the owner has already hydrated.
      if (isTauri() && getWindowRoleSync() !== "owner") {
        withMutationEmitSuppressed(clear);
      } else clear();
    },
  })),
);

/**
 * True while the desktop (Tauri) onboarding flow is still in progress, for
 * desktop UI that needs to hide during onboarding.
 *
 * The `stepHydrated` guard matters: the store initializes `step: null,
 * stepHydrated: false` before auth loads, so an already-onboarded desktop
 * user would otherwise have surfaces hidden during the startup/reconnect
 * window. After hydration, `step === null` means a genuinely fresh user
 * whose onboarding is about to start → active.
 */
export function useDesktopOnboardingActive(): boolean {
  return useOnboardingStore(
    (state) =>
      isTauri() &&
      state.stepHydrated &&
      state.step !== "completed" &&
      state.step !== "skipped",
  );
}
